import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail, API_BASE } from "@/lib/api";
import { formatINR } from "@/lib/format";
import { toast } from "sonner";
import {
  Truck,
  MapPin,
  Fuel,
  Droplet,
  Gauge,
  Camera,
  CreditCard,
  CheckCircle2,
  ArrowRight,
  ArrowLeft,
  X,
  Plus,
  QrCode,
  Loader2,
} from "lucide-react";

const PHOTO_LABELS = ["Bill Front", "Bill Back", "Meter Reading", "Pump Display"];

const STEPS = [
  { id: 1, label: "Truck & Station" },
  { id: 2, label: "Diesel & Urea" },
  { id: 3, label: "Odometer" },
  { id: 4, label: "Bill Photos" },
  { id: 5, label: "Payment" },
  { id: 6, label: "Review" },
];

function StepHeader({ step }) {
  return (
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2">
        {STEPS.map((s, idx) => (
          <div
            key={s.id}
            className={`h-1.5 rounded-full transition-all ${
              s.id < step ? "bg-bravura-500 w-6" :
              s.id === step ? "bg-bravura-500 w-10" : "bg-slate-200 w-4"
            }`}
          />
        ))}
      </div>
      <div className="text-xs text-slate-500 font-medium">Step {step}/6</div>
    </div>
  );
}

function FieldLabel({ children, required }) {
  return (
    <label className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold block mb-1.5">
      {children} {required && <span className="text-red-500">*</span>}
    </label>
  );
}

const inputCls =
  "w-full rounded-xl bg-white border border-slate-300 px-4 py-3 text-base font-semibold focus:outline-none focus:ring-2 focus:ring-bravura focus:border-bravura";

export default function FuelRefill() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [trucks, setTrucks] = useState([]);
  const [stations, setStations] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    truck_id: "",
    station_id: "",
    station_name: "",
    diesel_litres: "",
    diesel_rate: "",
    urea_litres: "",
    urea_amount: "",
    odometer_km: "",
    tank_full: false,
    payment_mode: "qr",
    upi_txn_ref: "",
    payment_proof_photo: "",
    pump_qr_photo: "",
    bill_photos: ["", "", "", ""],
    gps_lat: null,
    gps_lng: null,
    notes: "",
  });

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const reloadStations = async () => {
    try {
      const { data } = await api.get("/fuel/stations");
      setStations(data || []);
    } catch (e) {
      // silent
    }
  };

  // Initial load
  useEffect(() => {
    (async () => {
      try {
        const [t, s] = await Promise.all([
          api.get("/fuel/my-trucks"),
          api.get("/fuel/stations"),
        ]);
        setTrucks(t.data || []);
        setStations(s.data || []);
        // try GPS
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (pos) => update({ gps_lat: pos.coords.latitude, gps_lng: pos.coords.longitude }),
            () => {},
            { enableHighAccuracy: false, timeout: 4000 },
          );
        }
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load lookup data");
      }
    })();
  }, []);

  // Auto-fill diesel rate from selected station
  useEffect(() => {
    if (!form.station_id) return;
    const st = stations.find((s) => s.id === form.station_id);
    if (st?.default_diesel_rate && !form.diesel_rate) {
      update({ diesel_rate: String(st.default_diesel_rate), station_name: st.name });
    } else if (st) {
      update({ station_name: st.name });
    }
  }, [form.station_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const dieselAmount = useMemo(() => {
    const l = parseFloat(form.diesel_litres) || 0;
    const r = parseFloat(form.diesel_rate) || 0;
    return Math.round(l * r * 100) / 100;
  }, [form.diesel_litres, form.diesel_rate]);

  const urea = parseFloat(form.urea_amount) || 0;
  const totalAmount = Math.round((dieselAmount + urea) * 100) / 100;

  const selectedStation = stations.find((s) => s.id === form.station_id);
  const stationHasUpi = !!selectedStation?.upi_id;

  // Validation per step
  const canProceed = () => {
    if (step === 1) return !!form.truck_id && (!!form.station_id || !!form.station_name.trim());
    if (step === 2) return parseFloat(form.diesel_litres) > 0 && parseFloat(form.diesel_rate) > 0;
    if (step === 3) return parseInt(form.odometer_km, 10) > 0;
    if (step === 4) return true; // bill photos optional in v1
    if (step === 5) {
      if (form.payment_mode === "qr") return !!form.upi_txn_ref || stationHasUpi;
      return true;
    }
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        truck_id: form.truck_id,
        station_id: form.station_id || null,
        station_name: form.station_id ? null : form.station_name || null,
        diesel_litres: parseFloat(form.diesel_litres),
        diesel_rate: parseFloat(form.diesel_rate),
        urea_litres: form.urea_litres ? parseFloat(form.urea_litres) : null,
        urea_amount: form.urea_amount ? parseFloat(form.urea_amount) : null,
        odometer_km: parseInt(form.odometer_km, 10),
        tank_full: !!form.tank_full,
        payment_mode: form.payment_mode,
        upi_txn_ref: form.upi_txn_ref || null,
        payment_proof_photo: form.payment_proof_photo || null,
        pump_qr_photo: form.pump_qr_photo || null,
        bill_photos: form.bill_photos.filter(Boolean),
        gps_lat: form.gps_lat,
        gps_lng: form.gps_lng,
        notes: form.notes || null,
      };
      const { data } = await api.post("/fuel/refills", payload);
      toast.success("Fuel refill recorded ✓");
      navigate(`/driver/refills/${data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save refill");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="fuel-refill-wizard">
      <StepHeader step={step} />

      <div className="rounded-2xl bg-white ring-1 ring-slate-200 p-5 shadow-sm">
        {step === 1 && (
          <Step1
            form={form}
            update={update}
            trucks={trucks}
            stations={stations}
            reloadStations={reloadStations}
          />
        )}
        {step === 2 && (
          <Step2
            form={form}
            update={update}
            dieselAmount={dieselAmount}
          />
        )}
        {step === 3 && <Step3 form={form} update={update} />}
        {step === 4 && <Step4 form={form} update={update} />}
        {step === 5 && (
          <Step5
            form={form}
            update={update}
            totalAmount={totalAmount}
            selectedStation={selectedStation}
            stationHasUpi={stationHasUpi}
          />
        )}
        {step === 6 && (
          <Step6
            form={form}
            trucks={trucks}
            stations={stations}
            dieselAmount={dieselAmount}
            totalAmount={totalAmount}
          />
        )}
      </div>

      {/* Nav */}
      <div className="flex gap-3">
        {step > 1 && (
          <button
            onClick={() => setStep((s) => Math.max(1, s - 1))}
            data-testid="wizard-back-btn"
            className="flex-1 rounded-xl bg-slate-100 text-slate-700 px-4 py-3.5 font-semibold flex items-center justify-center gap-1.5 active:scale-[0.99]"
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
        )}
        {step < 6 && (
          <button
            onClick={() => canProceed() ? setStep((s) => s + 1) : toast.warning("Please fill required fields")}
            data-testid="wizard-next-btn"
            disabled={!canProceed()}
            className={`flex-1 rounded-xl px-4 py-3.5 font-semibold flex items-center justify-center gap-1.5 active:scale-[0.99] transition ${
              canProceed() ? "bg-bravura text-white shadow-md" : "bg-slate-200 text-slate-400"
            }`}
          >
            Next <ArrowRight className="h-4 w-4" />
          </button>
        )}
        {step === 6 && (
          <button
            onClick={submit}
            disabled={submitting}
            data-testid="wizard-submit-btn"
            className="flex-1 rounded-xl bg-emerald-600 text-white px-4 py-3.5 font-semibold flex items-center justify-center gap-2 active:scale-[0.99] shadow-md disabled:opacity-60"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
            Submit Refill
          </button>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Step 1 — Truck + Station (with GPS nearby + save-new-from-GPS)
// =============================================================================
function Step1({ form, update, trucks, stations, reloadStations }) {
  const [adHoc, setAdHoc] = useState(!!form.station_name && !form.station_id);
  const [nearby, setNearby] = useState([]);
  const [loadingNearby, setLoadingNearby] = useState(false);
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);

  // Load nearby whenever GPS is available
  useEffect(() => {
    if (!form.gps_lat || !form.gps_lng) return;
    setLoadingNearby(true);
    api.get("/fuel/stations/nearby", {
      params: { lat: form.gps_lat, lng: form.gps_lng, radius_km: 5 },
    })
      .then(({ data }) => setNearby(data || []))
      .catch(() => setNearby([]))
      .finally(() => setLoadingNearby(false));
  }, [form.gps_lat, form.gps_lng]);

  const saveNewFromGps = async () => {
    if (!newName.trim()) {
      toast.warning("Pump ka naam likhein");
      return;
    }
    if (!form.gps_lat) {
      toast.warning("GPS abhi nahi mila — phone ki location on karein");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/fuel/stations/from-gps", null, {
        params: {
          name: newName.trim(),
          lat: form.gps_lat,
          lng: form.gps_lng,
        },
      });
      toast.success("Naya pump save ho gaya ✓");
      // Auto-select it
      update({ station_id: data.id, station_name: data.name });
      setNewName("");
      // Refresh master list so the dropdown sees it
      if (reloadStations) await reloadStations();
      // Refresh nearby
      const { data: nd } = await api.get("/fuel/stations/nearby", {
        params: { lat: form.gps_lat, lng: form.gps_lng, radius_km: 5 },
      });
      setNearby(nd || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save fail ho gaya");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <div className="h-9 w-9 rounded-xl bg-slate-900 text-bravura-300 flex items-center justify-center">
          <Truck className="h-5 w-5" />
        </div>
        <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
          Which Truck & Station?
        </h2>
      </div>

      <div>
        <FieldLabel required>Truck</FieldLabel>
        <select
          className={inputCls}
          value={form.truck_id}
          onChange={(e) => update({ truck_id: e.target.value })}
          data-testid="truck-select"
        >
          <option value="">Select truck…</option>
          {trucks.map((t) => (
            <option key={t.id} value={t.id}>{t.truck_no} {t.model ? `— ${t.model}` : ""}</option>
          ))}
        </select>
      </div>

      {/* GPS-based nearby suggestion */}
      {form.gps_lat && (
        <div className="rounded-xl bg-bravura-50 ring-1 ring-bravura-200 p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] uppercase tracking-widest text-bravura-700 font-semibold flex items-center gap-1">
              <MapPin className="h-3 w-3" /> Nearby Pumps ({loadingNearby ? "…" : nearby.length})
            </div>
          </div>
          {nearby.length > 0 ? (
            <div className="space-y-1.5">
              {nearby.map((s) => (
                <button
                  key={s.id}
                  onClick={() => update({ station_id: s.id, station_name: s.name })}
                  data-testid={`nearby-station-${s.id}`}
                  className={`w-full text-left rounded-lg px-3 py-2 transition ring-1 ${
                    form.station_id === s.id
                      ? "bg-bravura text-white ring-bravura"
                      : "bg-white ring-slate-200"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold">{s.name}</span>
                    <span className={`text-[10px] ${form.station_id === s.id ? "text-bravura-100" : "text-slate-500"}`}>
                      {s.distance_km} km
                    </span>
                  </div>
                  {s.upi_id && (
                    <div className={`text-[10px] ${form.station_id === s.id ? "text-bravura-100" : "text-emerald-700"}`}>
                      ⚡ UPI: {s.upi_id}
                    </div>
                  )}
                </button>
              ))}
            </div>
          ) : !loadingNearby && (
            <p className="text-[11px] text-slate-600">
              5 km mein koi saved pump nahi mila. Niche se naam likh kar save kar dein.
            </p>
          )}
          {/* Add new station from GPS */}
          <div className="mt-3 pt-3 border-t border-bravura-200">
            <div className="text-[11px] uppercase tracking-widest text-bravura-700 font-semibold mb-1">
              + Naya pump add karein (current location se)
            </div>
            <div className="flex gap-2">
              <input
                className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bravura"
                placeholder="Pump ka naam (e.g. HP Sikar Road)"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                data-testid="new-station-name"
              />
              <button
                onClick={saveNewFromGps}
                disabled={saving}
                data-testid="save-new-station"
                className="rounded-lg bg-bravura text-white px-3 py-2 text-xs font-bold disabled:opacity-60"
              >
                {saving ? "…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-1.5">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
            Or pick from full list
          </div>
          <button
            onClick={() => { setAdHoc((v) => !v); update({ station_id: "", station_name: "" }); }}
            className="text-[11px] text-bravura font-medium"
            data-testid="toggle-adhoc-station"
          >
            {adHoc ? "Pick from list" : "Type manually"}
          </button>
        </div>
        {!adHoc ? (
          <select
            className={inputCls}
            value={form.station_id}
            onChange={(e) => update({ station_id: e.target.value })}
            data-testid="station-select"
          >
            <option value="">Select station…</option>
            {stations.map((s) => (
              <option key={s.id} value={s.id}>{s.name}{s.upi_id ? "  ⚡UPI" : ""}</option>
            ))}
          </select>
        ) : (
          <input
            className={inputCls}
            placeholder="Type station name"
            value={form.station_name}
            onChange={(e) => update({ station_name: e.target.value })}
            data-testid="station-name-input"
          />
        )}
      </div>

      {!form.gps_lat && (
        <div className="text-[11px] text-amber-700 bg-amber-50 ring-1 ring-amber-200 rounded-lg px-3 py-2">
          ⚠ GPS nahi mila. Phone ki location service ON karein.
        </div>
      )}
      {form.gps_lat && (
        <div className="text-[11px] text-emerald-700 flex items-center gap-1">
          <MapPin className="h-3 w-3" /> GPS captured ({form.gps_lat.toFixed(4)}, {form.gps_lng.toFixed(4)})
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Step 2 — Diesel + Urea
// =============================================================================
function Step2({ form, update, dieselAmount }) {
  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2">
        <div className="h-9 w-9 rounded-xl bg-bravura text-white flex items-center justify-center">
          <Fuel className="h-5 w-5" />
        </div>
        <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
          Diesel
        </h2>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <FieldLabel required>Litres</FieldLabel>
          <input
            type="number"
            inputMode="decimal"
            step="0.01"
            className={inputCls}
            value={form.diesel_litres}
            onChange={(e) => update({ diesel_litres: e.target.value })}
            placeholder="e.g. 120.50"
            data-testid="diesel-litres-input"
          />
        </div>
        <div>
          <FieldLabel required>Rate / L</FieldLabel>
          <input
            type="number"
            inputMode="decimal"
            step="0.01"
            className={inputCls}
            value={form.diesel_rate}
            onChange={(e) => update({ diesel_rate: e.target.value })}
            placeholder="e.g. 92.50"
            data-testid="diesel-rate-input"
          />
        </div>
      </div>

      <div className="rounded-xl bg-bravura-50 ring-1 ring-bravura-200 px-4 py-3 flex items-center justify-between">
        <span className="text-xs uppercase tracking-widest text-bravura-700 font-semibold">Diesel Amount</span>
        <span className="text-xl font-bold text-bravura-900" style={{ fontFamily: "Chivo" }}>
          ₹ {dieselAmount.toFixed(2)}
        </span>
      </div>

      <div className="flex items-center gap-2 pt-3 border-t border-slate-100">
        <div className="h-9 w-9 rounded-xl bg-sky-500 text-white flex items-center justify-center">
          <Droplet className="h-5 w-5" />
        </div>
        <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
          Urea / AdBlue <span className="text-xs font-normal text-slate-500">(optional)</span>
        </h2>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <FieldLabel>Litres</FieldLabel>
          <input
            type="number"
            inputMode="decimal"
            step="0.01"
            className={inputCls}
            value={form.urea_litres}
            onChange={(e) => update({ urea_litres: e.target.value })}
            placeholder="0"
            data-testid="urea-litres-input"
          />
        </div>
        <div>
          <FieldLabel>Amount (₹)</FieldLabel>
          <input
            type="number"
            inputMode="decimal"
            step="0.01"
            className={inputCls}
            value={form.urea_amount}
            onChange={(e) => update({ urea_amount: e.target.value })}
            placeholder="0"
            data-testid="urea-amount-input"
          />
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Step 3 — Odometer + Tank Full
// =============================================================================
function Step3({ form, update }) {
  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2">
        <div className="h-9 w-9 rounded-xl bg-slate-900 text-bravura-300 flex items-center justify-center">
          <Gauge className="h-5 w-5" />
        </div>
        <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
          Meter Reading
        </h2>
      </div>

      <div>
        <FieldLabel required>Odometer (km)</FieldLabel>
        <input
          type="number"
          inputMode="numeric"
          className={`${inputCls} text-3xl text-center font-mono`}
          value={form.odometer_km}
          onChange={(e) => update({ odometer_km: e.target.value })}
          placeholder="0"
          data-testid="odometer-input"
        />
        <p className="text-[11px] text-slate-500 mt-1.5">
          Look at the truck's dashboard and type the current km reading.
        </p>
      </div>

      <button
        type="button"
        onClick={() => update({ tank_full: !form.tank_full })}
        className={`w-full rounded-2xl p-4 flex items-center gap-3 ring-2 transition active:scale-[0.99] ${
          form.tank_full
            ? "bg-emerald-50 ring-emerald-500"
            : "bg-white ring-slate-200"
        }`}
        data-testid="tank-full-toggle"
      >
        <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${
          form.tank_full ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-500"
        }`}>
          {form.tank_full ? <CheckCircle2 className="h-5 w-5" /> : <Fuel className="h-5 w-5" />}
        </div>
        <div className="flex-1 text-left">
          <div className="text-sm font-bold text-slate-900">
            {form.tank_full ? "Tank Filled to Full ✓" : "Mark Tank Full?"}
          </div>
          <div className="text-[11px] text-slate-500">
            Mileage is calculated tank-full to tank-full
          </div>
        </div>
      </button>
    </div>
  );
}

// =============================================================================
// Step 4 — Bill Photos
// =============================================================================
function Step4({ form, update }) {
  const setPhoto = (idx, dataUrl) => {
    const arr = [...form.bill_photos];
    arr[idx] = dataUrl;
    update({ bill_photos: arr });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <div className="h-9 w-9 rounded-xl bg-slate-900 text-bravura-300 flex items-center justify-center">
          <Camera className="h-5 w-5" />
        </div>
        <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
          Bill & Reference Photos
        </h2>
      </div>
      <p className="text-xs text-slate-500 -mt-2">
        Tap each tile to capture (4 slots — all optional but recommended)
      </p>

      <div className="grid grid-cols-2 gap-3">
        {PHOTO_LABELS.map((lbl, idx) => (
          <PhotoTile
            key={idx}
            label={lbl}
            value={form.bill_photos[idx]}
            onCapture={(d) => setPhoto(idx, d)}
            onClear={() => setPhoto(idx, "")}
            testId={`photo-tile-${idx}`}
          />
        ))}
      </div>

      <div>
        <FieldLabel>Notes (optional)</FieldLabel>
        <textarea
          rows={2}
          className={inputCls}
          value={form.notes}
          onChange={(e) => update({ notes: e.target.value })}
          placeholder="Any observation, anomaly, route, etc."
          data-testid="notes-input"
        />
      </div>
    </div>
  );
}

function PhotoTile({ label, value, onCapture, onClear, testId }) {
  // Vivo Origin OS + some Android WebViews block camera intent when
  // triggered via `ref.click()` on a hidden <input>. Using a native
  // <label htmlFor> makes the click originate directly on the input,
  // which is required by strict user-gesture policies. We also drop
  // `capture="environment"` — the intent-only value can silently fail
  // on Vivo. Chrome mobile still opens the camera picker with just
  // `accept="image/*"`; on stock Vivo it shows Camera/Gallery chooser
  // so the driver taps Camera.
  const inputRef = useRef(null);
  // Stable ID per instance so <label htmlFor> works even in React StrictMode
  const inputId = useMemo(
    () => `photo-tile-${testId || Math.random().toString(36).slice(2, 9)}`,
    [testId],
  );
  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    // Compress to ~800px wide JPEG to keep payload small
    const img = new Image();
    const reader = new FileReader();
    reader.onload = () => {
      img.onload = () => {
        const MAX_W = 900;
        const scale = Math.min(1, MAX_W / img.width);
        const c = document.createElement("canvas");
        c.width = img.width * scale;
        c.height = img.height * scale;
        const ctx = c.getContext("2d");
        ctx.drawImage(img, 0, 0, c.width, c.height);
        const out = c.toDataURL("image/jpeg", 0.7);
        onCapture(out);
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(f);
  };
  return (
    <div className="relative">
      <label
        htmlFor={inputId}
        data-testid={testId}
        className={`w-full aspect-square rounded-2xl border-2 border-dashed transition flex flex-col items-center justify-center gap-1 cursor-pointer select-none ${
          value ? "border-emerald-500 bg-emerald-50" : "border-slate-300 bg-slate-50 active:bg-slate-100"
        }`}
      >
        {value ? (
          <img src={value} alt={label} className="w-full h-full object-cover rounded-2xl" />
        ) : (
          <>
            <Camera className="h-7 w-7 text-slate-400" />
            <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</span>
            <Plus className="h-4 w-4 text-bravura" />
          </>
        )}
      </label>
      {value && (
        <button
          type="button"
          onClick={onClear}
          className="absolute top-1 right-1 h-7 w-7 rounded-full bg-red-600 text-white flex items-center justify-center shadow"
          data-testid={`${testId}-clear`}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
      <input
        id={inputId}
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={onPick}
        className="sr-only"
      />
      <div className="absolute bottom-1 left-1 right-1 text-center pointer-events-none">
        {value && (
          <span className="text-[9px] uppercase tracking-widest text-white bg-emerald-600/80 rounded px-1.5 py-0.5">
            {label}
          </span>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Step 5 — Payment + UPI QR
// =============================================================================
function Step5({ form, update, totalAmount, selectedStation, stationHasUpi }) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <div className="h-9 w-9 rounded-xl bg-slate-900 text-bravura-300 flex items-center justify-center">
          <CreditCard className="h-5 w-5" />
        </div>
        <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
          Payment Method
        </h2>
      </div>

      <div className="rounded-xl bg-slate-900 text-white p-4 text-center">
        <div className="text-[11px] uppercase tracking-widest text-slate-400">Total to Pay</div>
        <div className="text-3xl font-bold mt-1" style={{ fontFamily: "Chivo" }}>
          ₹ {totalAmount.toFixed(2)}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2">
        {[
          { v: "qr", label: "UPI QR", icon: QrCode },
          { v: "cash", label: "Cash", icon: CreditCard },
          { v: "imprest", label: "Imprest", icon: CreditCard },
        ].map((m) => (
          <button
            key={m.v}
            onClick={() => update({ payment_mode: m.v })}
            data-testid={`pm-${m.v}`}
            className={`rounded-xl p-3 ring-2 transition active:scale-[0.99] flex flex-col items-center gap-1.5 ${
              form.payment_mode === m.v
                ? "bg-bravura-50 ring-bravura"
                : "bg-white ring-slate-200"
            }`}
          >
            <m.icon className={`h-5 w-5 ${form.payment_mode === m.v ? "text-bravura-700" : "text-slate-500"}`} />
            <span className="text-xs font-semibold text-slate-700">{m.label}</span>
          </button>
        ))}
      </div>

      {form.payment_mode === "qr" && (
        <div className="rounded-2xl bg-white ring-1 ring-slate-200 p-4 space-y-3">
          {!stationHasUpi && !selectedStation?.upi_qr_photo && (
            <div className="space-y-2">
              <div className="text-xs text-amber-800 bg-amber-50 ring-1 ring-amber-200 rounded-lg px-3 py-2">
                ℹ Is pump ke liye admin ne UPI configure nahi kiya. Aap pump ka QR sticker photo click karke yahin add kar do — admin ko bhi mil jayega.
              </div>
              <div className="w-44">
                <PhotoTile
                  label="Pump ka QR Photo"
                  value={form.pump_qr_photo}
                  onCapture={(d) => update({ pump_qr_photo: d })}
                  onClear={() => update({ pump_qr_photo: "" })}
                  testId="driver-pump-qr"
                />
              </div>
              {form.pump_qr_photo && (
                <div className="text-[11px] text-emerald-700">
                  ✓ Saved. Ab is QR ko phone se scan karke pay karein.
                </div>
              )}
            </div>
          )}
          {!stationHasUpi && selectedStation?.upi_qr_photo && (
            <div className="flex flex-col items-center gap-2">
              <img
                src={selectedStation.upi_qr_photo}
                alt="UPI QR"
                className="w-48 h-48 object-contain rounded-xl ring-1 ring-slate-200 bg-white"
                data-testid="upi-qr-photo-uploaded"
              />
              <div className="text-[11px] text-slate-500 text-center">
                Pump ka QR scan karein → ₹{totalAmount.toFixed(2)} pay karein
              </div>
              <div className="text-[10px] text-amber-600">
                ℹ Admin uploaded QR — amount manually enter karna hoga
              </div>
            </div>
          )}
          {stationHasUpi && selectedStation?.id && (
            <div className="flex flex-col items-center gap-2">
              <img
                src={`${API_BASE}/fuel/qr-preview.png?station_id=${selectedStation.id}&amount=${totalAmount}&truck_no=${encodeURIComponent("")}`}
                alt="UPI QR"
                className="w-48 h-48 rounded-xl ring-1 ring-slate-200"
                data-testid="upi-qr-image"
              />
              <div className="text-[11px] text-slate-500 text-center">
                Scan with any UPI app to pay ₹{totalAmount.toFixed(2)} to <b>{selectedStation.upi_payee_name || selectedStation.name}</b>
              </div>
            </div>
          )}
          <div>
            <FieldLabel>UPI Reference (after payment)</FieldLabel>
            <input
              className={inputCls}
              value={form.upi_txn_ref}
              onChange={(e) => update({ upi_txn_ref: e.target.value })}
              placeholder="e.g. 401234567890"
              data-testid="upi-txn-input"
            />
          </div>

          <div>
            <FieldLabel>
              Payment Screenshot
              <span className="ml-1 text-[10px] font-normal text-slate-500 normal-case">
                (UPI success page ya transaction screenshot click karein)
              </span>
            </FieldLabel>
            <div className="mt-1 w-44">
              <PhotoTile
                label="Payment Proof"
                value={form.payment_proof_photo}
                onCapture={(d) => update({ payment_proof_photo: d })}
                onClear={() => update({ payment_proof_photo: "" })}
                testId="upi-payment-proof"
              />
            </div>
          </div>
        </div>
      )}

      {form.payment_mode === "cash" && (
        <div className="rounded-2xl bg-white ring-1 ring-slate-200 p-4 space-y-3">
          <div>
            <FieldLabel>
              Cash Receipt Photo
              <span className="ml-1 text-[10px] font-normal text-slate-500 normal-case">
                (optional — pump ki cash receipt click karein)
              </span>
            </FieldLabel>
            <div className="mt-1 w-44">
              <PhotoTile
                label="Cash Receipt"
                value={form.payment_proof_photo}
                onCapture={(d) => update({ payment_proof_photo: d })}
                onClear={() => update({ payment_proof_photo: "" })}
                testId="cash-receipt-photo"
              />
            </div>
          </div>
        </div>
      )}

      {form.payment_mode === "imprest" && (
        <div className="text-xs text-bravura-700 bg-bravura-50 ring-1 ring-bravura-200 rounded-lg px-3 py-2">
          ℹ Imprest refills will be marked <b>Pending</b> until Logistics Manager approves.
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Step 6 — Review
// =============================================================================
function Step6({ form, trucks, stations, dieselAmount, totalAmount }) {
  const tr = trucks.find((t) => t.id === form.truck_id);
  const st = stations.find((s) => s.id === form.station_id);
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <div className="h-9 w-9 rounded-xl bg-emerald-500 text-white flex items-center justify-center">
          <CheckCircle2 className="h-5 w-5" />
        </div>
        <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
          Review & Submit
        </h2>
      </div>

      <div className="rounded-2xl bg-slate-900 text-white p-4 space-y-2">
        <Row k="Truck" v={tr?.truck_no || "—"} />
        <Row k="Station" v={st?.name || form.station_name || "—"} />
        <Row k="Diesel" v={`${form.diesel_litres} L @ ₹${form.diesel_rate} = ₹${dieselAmount.toFixed(2)}`} />
        {form.urea_amount && <Row k="Urea" v={`${form.urea_litres || "?"} L = ₹${form.urea_amount}`} />}
        <Row k="Odometer" v={`${form.odometer_km} km${form.tank_full ? " (TANK FULL)" : ""}`} />
        <Row k="Payment" v={form.payment_mode.toUpperCase()} />
        <div className="border-t border-white/10 pt-2 mt-2 flex items-baseline justify-between">
          <span className="text-xs uppercase tracking-widest text-slate-400">Total</span>
          <span className="text-2xl font-bold text-bravura-300" style={{ fontFamily: "Chivo" }}>
            ₹ {totalAmount.toFixed(2)}
          </span>
        </div>
      </div>

      {form.payment_proof_photo && (
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-1.5">
            Payment Proof
          </div>
          <img
            src={form.payment_proof_photo}
            alt="Payment proof"
            className="w-40 aspect-square object-cover rounded-lg ring-1 ring-emerald-300"
          />
        </div>
      )}

      {form.bill_photos.filter(Boolean).length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-1.5">
            Photos ({form.bill_photos.filter(Boolean).length}/4)
          </div>
          <div className="grid grid-cols-4 gap-2">
            {form.bill_photos.map((p, i) => p && (
              <img key={i} src={p} alt="" className="aspect-square object-cover rounded-lg ring-1 ring-slate-200" />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-baseline justify-between text-sm">
      <span className="text-slate-400">{k}</span>
      <span className="font-semibold text-right">{v}</span>
    </div>
  );
}
